import type { TileRef } from "../document/types.ts";
import { packOf } from "../editor/packs.ts";

/**
 * Props made from tiles (ADR-102): one pack's tile references gathered into a prop that places, flips, blocks and
 * draws like any other. This module holds the plain data a pack's library document is made of and the names that
 * tie an entry to its frame. Nothing here reads a pixel or names a game.
 */

/**
 * The name of the collection-shaped tileset a pack gains for its entries, after the pack prefix (ADR-102 decision 7).
 */
export const TILE_PROPS_TILESET = "tile-props";

/**
 * The layout this build reads and writes.
 */
export const TILE_PROPS_SCHEMA_VERSION = 1;

/**
 * The most columns or rows an entry spans (ADR-102 decision 3).
 */
export const TILE_PROP_SIDE_MAX = 30;

/**
 * The most cells an entry holds.
 */
export const TILE_PROP_CELLS_MAX = 2048;

/**
 * The most cells that share one place.
 */
export const TILE_PROP_STACK_MAX = 4;

/**
 * The most characters in an entry's name or group.
 */
export const TILE_PROP_TEXT_MAX = 64;

/**
 * One cell of an entry: a whole-tile place counted from the top-left of the frame, and the tile drawn there. The
 * tile is a `TileRef` as the rest of Daedalus has it, its flags mirroring the tile (ADR-089).
 */
export interface TilePropCell {
  readonly x: number;
  readonly y: number;
  readonly tile: TileRef;
}

/**
 * One prop made from tiles. `id` is the frame's identity and never changes; `rev` counts the entry's own writes.
 * `cells` draw in list order, a later cell over an earlier one at the same place. `base` is optional, whole pixels
 * from the frame's top; absent, the build measures it. `retired` is present, and true, on an entry that left the
 * palette and keeps drawing wherever a world still places it (ADR-102 decision 24).
 */
export interface TilePropEntry {
  readonly id: number;
  readonly rev: number;
  readonly name: string;
  readonly group: string;
  readonly cols: number;
  readonly rows: number;
  readonly base?: number;
  readonly retired?: true;
  readonly cells: readonly TilePropCell[];
}

/**
 * A pack's tile props document (ADR-102 decision 2). `next` is the id the next new entry takes; an entry is never
 * removed and an id never reused.
 */
export interface TileProps {
  readonly schemaVersion: 1;
  readonly pack: string;
  readonly next: number;
  readonly props: readonly TilePropEntry[];
}

/**
 * What a reader may know of a tileset in the pack: the size of its tiles, and whether it is a grid (an image
 * collection is not). A host hands it in where it has imported the tilesets; a tileset it does not know is left to
 * the build, which names it when it cannot compose the entry (ADR-102 decision 9).
 */
export interface TilePropSheet {
  readonly tileSize: number;
  readonly grid: boolean;
}

/**
 * A lookup of a pack's tilesets by id.
 */
export type TilePropSheets = (tileset: string) => TilePropSheet | undefined;

/**
 * The id of a pack's tile props tileset.
 *
 * @param pack The pack's id.
 * @returns `<pack>:tile-props`.
 */
export function tilePropsTilesetId(pack: string): string {
  return `${pack}:${TILE_PROPS_TILESET}`;
}

/**
 * Whether a tileset id is a pack's tile props tileset. A game's rules read this from a frame's tileset to tell
 * a composed frame from a drawn one (ADR-102 decisions 18 and 19).
 *
 * @param tilesetId A tileset id.
 * @returns True for `<pack>:tile-props` with a non-empty pack.
 */
export function isTilePropsTileset(tilesetId: string): boolean {
  const pack = packOf(tilesetId);
  return pack !== null && tilesetId.slice(pack.length + 1) === TILE_PROPS_TILESET;
}

/**
 * The frame reference of an entry: a tile of the pack's tile props tileset, named by the entry's id.
 *
 * @param pack The pack's id.
 * @param id The entry's id.
 * @returns The reference.
 */
export function tilePropFrame(pack: string, id: number): TileRef {
  return { tileset: tilePropsTilesetId(pack), tile: id };
}
